import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDeleteTeam, useTeam, useTeamMembers } from "@/hooks/queries/use-teams";
import { useAuthStore } from "@/stores/auth-store";
import { TeamBreadcrumb } from "@/components/teams/team-breadcrumb";
import { TeamMembersTab } from "@/components/teams/team-members-tab";
import { TeamOverviewTab } from "@/components/teams/team-overview-tab";
import { TeamEpicsTab } from "@/components/teams/team-epics-tab";
import { usePageTitle } from "@/hooks/use-page-title";
import { TeamForm } from "@/components/teams/team-form";
import { DisciplineFormModal } from "@/components/teams/discipline-form-modal";
import { DeleteDisciplineDialog } from "@/components/teams/delete-discipline-dialog";
import { AddMemberModal } from "@/components/teams/add-member-modal";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Pencil, Sparkles, Trash2 } from "lucide-react";
import type { Team } from "@/lib/api/types";
import { getInitials } from "@/lib/get-initials";
import { getChildTeamLevel, getTeamLevelBadgeClass, getTeamLevelLabel } from "@/lib/team-hierarchy";
import { getTeamHueColor } from "@/lib/team-hue";

type TeamDetailTab = "overview" | "members" | "epics";

const TEAM_DETAIL_TABS = new Set<TeamDetailTab>(["overview", "members", "epics"]);

function getTeamDescription(team: Team): string {
  const childCount = team.childCount ?? 0;

  switch (team.level) {
    case "group":
      return `Top-level organizational group encompassing ${childCount} product ${childCount === 1 ? "line" : "lines"}.`;
    case "product-line":
      return "Product line organizing related products and their engineering disciplines.";
    case "product":
      return `Product with ${childCount} discipline${childCount === 1 ? "" : "s"} actively building and maintaining features.`;
    case "discipline":
      return team.canOwnEpics
        ? "This discipline owns and delivers epics. Review active work, members, and requests below."
        : "Functional team within the product hierarchy.";
    default:
      return "Explore this team's overview, members, epics, and settings using the tabs below.";
  }
}

/**
 * Inline panel that renders a team's detail content.
 * Used inside the split-panel TeamsPage layout.
 */
export function TeamDetailPanel({ teamId }: { teamId: string }) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: team, isLoading } = useTeam(teamId);
  const { data: members } = useTeamMembers(teamId);
  usePageTitle(team?.name ?? "Team");
  const deleteTeam = useDeleteTeam();
  const { user } = useAuthStore();
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [showAddChildDialog, setShowAddChildDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showAddMember, setShowAddMember] = useState(false);

  const tabParam = searchParams.get("tab");
  const activeTab = TEAM_DETAIL_TABS.has(tabParam as TeamDetailTab)
    ? (tabParam as TeamDetailTab)
    : "overview";
  const isAdmin = user?.isGlobalAdmin ?? false;

  const teamMembers = members ?? [];
  const memberCount = team?._count?.memberships ?? 0;
  const displayedMemberCount = teamMembers.length > 0 ? teamMembers.length : memberCount;

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="h-40 animate-pulse rounded-2xl bg-muted/60" />
        <div className="h-80 animate-pulse rounded-2xl bg-muted/60" />
      </div>
    );
  }

  if (!team) {
    return (
      <div className="flex h-full items-center justify-center">
        <p className="text-muted-foreground">Team not found</p>
      </div>
    );
  }

  const handleDelete = async () => {
    await deleteTeam.mutateAsync(team.id);
    navigate("/teams");
  };

  const handleTabChange = (value: string) => {
    if (!TEAM_DETAIL_TABS.has(value as TeamDetailTab)) {
      return;
    }

    const nextSearchParams = new URLSearchParams(searchParams);
    if (value === "overview") {
      nextSearchParams.delete("tab");
    } else {
      nextSearchParams.set("tab", value);
    }

    setSearchParams(nextSearchParams, { replace: true });
  };

  const hueColor = getTeamHueColor(team.name);
  const teamDescription = getTeamDescription(team);
  const childLevel = getChildTeamLevel(team.level);

  return (
    <div className="relative">
      <div
        className="pointer-events-none absolute inset-0 z-0 rounded-2xl"
        style={{
          background: `radial-gradient(ellipse 140% 50% at 50% -5%, ${hueColor}2B, transparent 70%)`,
        }}
      />

      <div className="relative z-10 space-y-4">
        <div className="glass-card rounded-2xl p-4 md:p-6">
          <div className="mb-4">
            <TeamBreadcrumb teamId={team.id} />
          </div>

          <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
            <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
              <div className="space-y-3">
                <div className="flex flex-wrap items-center gap-2">
                  <h1 className="text-2xl font-semibold text-foreground">{team.name}</h1>
                  <Badge
                    variant="outline"
                    className={`border px-2 py-0 text-[10px] font-medium ${getTeamLevelBadgeClass(team.level)}`}
                  >
                    {getTeamLevelLabel(team.level)}
                  </Badge>
                  {team.canOwnEpics ? (
                    <Badge
                      variant="outline"
                      className="border-emerald-500/20 bg-emerald-500/10 text-emerald-200"
                    >
                      <Sparkles className="mr-1 h-3 w-3" />
                      Epic owner
                    </Badge>
                  ) : null}
                </div>

                <div className="mt-2 flex flex-wrap items-center gap-3">
                  {teamMembers.length > 0 ? (
                    <div className="flex -space-x-2">
                      {teamMembers.slice(0, 5).map((member) => (
                        <Avatar key={member.id} className="h-7 w-7 border-2 border-background">
                          {member.user.avatarUrl ? (
                            <AvatarImage src={member.user.avatarUrl} alt={member.user.name} />
                          ) : null}
                          <AvatarFallback className="bg-white/[0.08] text-[10px] font-medium text-foreground">
                            {getInitials(member.user.name)}
                          </AvatarFallback>
                        </Avatar>
                      ))}
                      {teamMembers.length > 5 ? (
                        <div className="flex h-7 w-7 items-center justify-center rounded-full border-2 border-background bg-white/[0.08] text-[10px] font-medium text-muted-foreground">
                          +{teamMembers.length - 5}
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                  <span className="text-xs text-muted-foreground">
                    {displayedMemberCount} member{displayedMemberCount !== 1 ? "s" : ""}
                  </span>
                </div>

                <p className="max-w-3xl text-sm text-muted-foreground">{teamDescription}</p>
              </div>

              {isAdmin ? (
                <div className="flex flex-wrap items-center gap-2">
                  {childLevel ? (
                    <Button variant="outline" onClick={() => setShowAddChildDialog(true)}>
                      <Sparkles className="mr-2 h-4 w-4" />
                      Add child team
                    </Button>
                  ) : null}
                  <Button variant="outline" onClick={() => setIsEditOpen(true)}>
                    <Pencil className="mr-2 h-4 w-4" />
                    Edit
                  </Button>
                  <Button variant="outline" onClick={() => setShowDeleteDialog(true)}>
                    <Trash2 className="mr-2 h-4 w-4" />
                    Delete
                  </Button>
                </div>
              ) : null}
            </div>

            <div className="my-4 border-t border-white/[0.06]" />

            <TabsList className="mb-6 flex h-auto w-full justify-start gap-1 overflow-x-auto">
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="members">
                Members
                <span className="ml-2 rounded-full bg-background/80 px-2 py-0.5 text-xs leading-none text-foreground">
                  {displayedMemberCount}
                </span>
              </TabsTrigger>
              <TabsTrigger value="epics">Epics &amp; Requests</TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="mt-0">
              <TeamOverviewTab teamId={team.id} />
            </TabsContent>

            <TabsContent value="members" className="mt-0">
              <TeamMembersTab
                teamId={team.id}
                currentUserId={user?.id ?? ""}
                onAddMember={() => setShowAddMember(true)}
              />
            </TabsContent>

            <TabsContent value="epics" className="mt-0">
              <TeamEpicsTab teamId={team.id} />
            </TabsContent>
          </Tabs>
        </div>

        <DisciplineFormModal
          discipline={team}
          open={isEditOpen && team.level === "discipline"}
          onOpenChange={setIsEditOpen}
        />

        <TeamForm
          team={team}
          open={isEditOpen && team.level !== "discipline"}
          onOpenChange={setIsEditOpen}
        />

        <TeamForm
          parentTeam={team}
          open={showAddChildDialog}
          onOpenChange={setShowAddChildDialog}
        />

        <DeleteDisciplineDialog
          teamId={team.id}
          team={team}
          open={showDeleteDialog && team.level === "discipline"}
          onOpenChange={setShowDeleteDialog}
          onDeleted={() => navigate("/teams")}
        />

        <Dialog
          open={showDeleteDialog && team.level !== "discipline"}
          onOpenChange={setShowDeleteDialog}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Delete Team</DialogTitle>
              <DialogDescription>
                Are you sure you want to delete &quot;{team.name}&quot;? This will remove all
                members and archive all epics in this team. This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={() => void handleDelete()}
                disabled={deleteTeam.isPending}
              >
                {deleteTeam.isPending ? "Deleting..." : "Delete"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <AddMemberModal teamId={team.id} open={showAddMember} onOpenChange={setShowAddMember} />
      </div>
    </div>
  );
}

/**
 * @deprecated Kept for backward compatibility with the router.
 * The TeamsPage now handles both /teams and /teams/:teamId.
 */
export function TeamDetailPage() {
  return null;
}
